import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { GitMerge, TriangleAlert } from "lucide-react";
import { Grid, Skeleton, Box, Divider } from "@mui/material";

interface Tag {
  tagName: string;
  colour: string;
}

interface PatientLike {
  id: string;
  name?: string;
  dob?: string;
  age?: number;
  nhs?: string;
  address?: string;
  contact: string;
  phone?: string;
  email?: string;
  tags?: Tag[];
  allergies?: string[];
  consultationsCount?: number;
  practitioner?: string;
  isConsultationActive?: string;
}

export interface MergePatientsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientA: PatientLike;
  patientB: PatientLike;
  isDarkMode: boolean;
  onConfirm: (merged: PatientLike) => void;
  mergePatientDetailsLoader: boolean;
  patientConsultationCountLoading: boolean;
  showWarningDialog: boolean;
  setShowWarningDialog: (showWarningDialog: boolean) => void;
}

export default function MergePatientsModal({
  open,
  onOpenChange,
  patientA,
  patientB,
  isDarkMode,
  onConfirm,
  mergePatientDetailsLoader,
  patientConsultationCountLoading,
  showWarningDialog,
  setShowWarningDialog,
}: MergePatientsModalProps) {
  const baseFields: { key: keyof PatientLike; label: string }[] = [
    { key: "name", label: "Name" },
    { key: "dob", label: "Date of Birth" },
    { key: "age", label: "Age" },
    { key: "nhs", label: "NHS Number" },
    { key: "address", label: "Address" },
    { key: "contact", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "practitioner", label: "Practitioner" },
    { key: "isConsultationActive", label: "Status" },
  ];

  const [choices, setChoices] = useState<Record<string, "A" | "B">>({});
  const [mergeTagsA, setMergeTagsA] = useState(true);
  const [mergeTagsB, setMergeTagsB] = useState(true);
  const [mergeAllergiesA, setMergeAllergiesA] = useState(true);
  const [mergeAllergiesB, setMergeAllergiesB] = useState(true);
  const [keepConsultationsA, setKeepConsultationsA] = useState(true);
  const [keepConsultationsB, setKeepConsultationsB] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState<"A" | "B" | null>(null);
  const [hideSameValues, setHideSameValues] = useState(false);

  function formatValue(v: any): string {
    if (v === null || v === undefined) return "-";
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    return String(v);
  }

  const areValuesEqual = (
    valA: any,
    valB: any,
    isArray: boolean = false,
  ): boolean => {
    if (isArray) {
      if (!Array.isArray(valA) || !Array.isArray(valB)) return false;
      if (valA.length !== valB.length) return false;
      const setA = new Set(valA);
      const setB = new Set(valB);
      if (setA.size !== setB.size) return false;
      for (const item of setA) {
        if (!setB.has(item)) return false;
      }
      return true;
    }
    return valA === valB;
  };

  useEffect(() => {
    if (open) {
      const initial: Record<string, "A" | "B"> = {};
      for (const f of baseFields) {
        const valA = (patientA as any)[f.key];
        const valB = (patientB as any)[f.key];
        if (valA === valB) {
          initial[f.key as string] = "A";
        }
      }
      setChoices(initial);
      setSelectedColumn(null);
      setShowWarningDialog(false);

      const tagsAreSame = areValuesEqual(patientA.tags, patientB.tags, true);
      const allergiesAreSame = areValuesEqual(
        patientA.allergies,
        patientB.allergies,
        true,
      );
      const consultationsAreSame = areValuesEqual(
        patientA.consultationsCount,
        patientB.consultationsCount,
      );

      setMergeTagsA(tagsAreSame);
      setMergeTagsB(tagsAreSame);
      setMergeAllergiesA(allergiesAreSame);
      setMergeAllergiesB(allergiesAreSame);
      setKeepConsultationsA(consultationsAreSame);
      setKeepConsultationsB(consultationsAreSame);
    }
  }, [open, patientA, patientB]);

  const mergedPreview = useMemo(() => {
    const result: PatientLike & {
      patientID: string;
      otherPatientID: string;
      mergeTags: boolean;
      mergeAllergies: boolean;
    } = {
      patientID: patientA.id,
      otherPatientID: patientB.id,
      mergeTags: mergeTagsA,
      mergeAllergies: mergeAllergiesA,
      tags: [],
      allergies: [],
    } as any;

    // Base fields merge
    for (const { key } of baseFields) {
      const pick = choices[key as string] || "A";
      (result as any)[key] =
        pick === "A" ? (patientA as any)[key] : (patientB as any)[key];
    }

    if (mergeTagsA) {
      result.tags = patientA.tags || [];
    } else {
      result.tags = patientB.tags || [];
    }

    if (mergeAllergiesA) {
      result.allergies = patientA.allergies || [];
    } else {
      result.allergies = patientB.allergies || [];
    }

    let consultationsCount = 0;
    if (keepConsultationsA)
      consultationsCount += patientA.consultationsCount || 0;
    if (keepConsultationsB)
      consultationsCount += patientB.consultationsCount || 0;
    result.consultationsCount = consultationsCount;

    return result;
  }, [
    choices,
    mergeTagsA,
    mergeAllergiesA,
    keepConsultationsA,
    keepConsultationsB,
    baseFields,
    patientA,
    patientB,
  ]);

  const labelClass = isDarkMode ? "text-gray-300" : "text-gray-600";
  const cellClass = isDarkMode ? "text-white" : "text-gray-900";
  const borderClass = isDarkMode ? "border-gray-700" : "border-gray-200";

  const allRequiredFieldsSelected = useMemo(() => {
    for (const { key } of baseFields) {
      const valA = (patientA as any)[key];
      const valB = (patientB as any)[key];
      const isSame = areValuesEqual(valA, valB);

      if (!isSame && !choices[key as string]) {
        return false;
      }
    }

    if (!mergeTagsA && !mergeTagsB) {
      return false;
    }

    if (!mergeAllergiesA && !mergeAllergiesB) {
      return false;
    }

    return true;
  }, [
    baseFields,
    choices,
    patientA,
    patientB,
    mergeTagsA,
    mergeTagsB,
    mergeAllergiesA,
    mergeAllergiesB,
  ]);

  const selectAllFromPatient = (patient: "A" | "B") => {
    const newChoices: Record<string, "A" | "B"> = { ...choices };

    for (const f of baseFields) {
      const valA = (patientA as any)[f.key];
      const valB = (patientB as any)[f.key];
      const isSame = areValuesEqual(valA, valB);

      if (!isSame) {
        newChoices[f.key as string] = patient;
      }
    }

    setChoices(newChoices);
    setSelectedColumn(patient);

    const tagsAreSame = areValuesEqual(patientA.tags, patientB.tags, true);
    const allergiesAreSame = areValuesEqual(
      patientA.allergies,
      patientB.allergies,
      true,
    );
    const consultationsAreSame = areValuesEqual(
      patientA.consultationsCount,
      patientB.consultationsCount,
    );

    if (!tagsAreSame) {
      setMergeTagsA(patient === "A");
      setMergeTagsB(patient === "B");
    }

    if (!allergiesAreSame) {
      setMergeAllergiesA(patient === "A");
      setMergeAllergiesB(patient === "B");
    }

    if (!consultationsAreSame) {
      setKeepConsultationsA(patient === "A");
      setKeepConsultationsB(patient === "B");
    }
  };

  const SkeletonLoader = () => {
    return (
      <>
        {/* Description */}
        <Skeleton width="90%" height={18} />
        <Skeleton width="75%" height={18} />

        {/* Patient summary cards */}
        <Grid container spacing={2}>
          {[0, 1].map((i) => (
            <Grid item xs={6} key={i}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Skeleton width="60%" height={22} />
                <Skeleton width="50%" height={16} />
                <Skeleton width="45%" height={16} />
                <Skeleton width="40%" height={16} />

                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {[0, 1, 2].map((t) => (
                    <Skeleton
                      key={t}
                      variant="rounded"
                      width={60}
                      height={20}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Hide same values toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton width={200} height={18} />
        </Box>

        {/* Field chooser table */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Grid container sx={{ p: 1 }}>
            <Grid item xs={4}>
              <Skeleton width={80} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={120} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={120} />
            </Grid>
          </Grid>

          <Divider />

          {/* Rows */}
          {[...Array(6)].map((_, idx) => (
            <Box key={idx}>
              <Grid container alignItems="center" sx={{ p: 1 }}>
                <Grid item xs={4}>
                  <Skeleton width={100} />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton width="80%" />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton width="80%" />
                </Grid>
              </Grid>
              <Divider />
            </Box>
          ))}

          {/* Tags row */}
          <Grid container sx={{ p: 1 }}>
            <Grid item xs={4}>
              <Skeleton width={60} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={140} />
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                {[0, 1].map((i) => (
                  <Skeleton key={i} variant="rounded" width={50} height={20} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={140} />
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                {[0, 1].map((i) => (
                  <Skeleton key={i} variant="rounded" width={50} height={20} />
                ))}
              </Box>
            </Grid>
          </Grid>

          <Divider />

          {/* Consultations row */}
          <Grid container sx={{ p: 1 }}>
            <Grid item xs={4}>
              <Skeleton width={120} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={160} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width={160} />
            </Grid>
          </Grid>
        </Box>

        {/* Merged preview */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            minHeight: 180,
          }}
        >
          <Skeleton width={120} height={18} sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton width="80%" />
              <Skeleton width="70%" />
              <Skeleton width="60%" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="80%" />
              <Skeleton width="70%" />
              <Skeleton width="60%" />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" width={60} height={20} />
            ))}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setShowWarningDialog(false);
          }
          onOpenChange(isOpen);
        }}
      >
        <DialogContent
          className={`${
            isDarkMode ? "bg-gray-900 text-white" : "bg-white"
          } max-w-4xl max-h-[85vh] p-4 overflow-hidden flex flex-col`}
        >
          {patientConsultationCountLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              <DialogHeader className="space-y-3">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <GitMerge className="w-5 h-5" />
                  Merge Patients
                </DialogTitle>
                <DialogDescription className={labelClass}>
                  Select the values to keep in the merged patient record. The
                  other patient will be archived. Fields marked with{" "}
                  <span className="text-red-500">*</span> require your
                  selection.
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 min-h-0 pr-2 space-y-4 overflow-y-auto">
                {/* Patients summary */}
                <div className={`grid grid-cols-2 gap-4`}>
                  {[patientA, patientB].map((p, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg border ${borderClass} p-4`}
                    >
                      <div className="mb-1 text-base font-semibold">
                        {p.name || "Unnamed"}
                      </div>
                      <div className={`text-sm ${labelClass}`}>
                        NHS: {p.nhs || "-"}
                      </div>
                      <div className={`text-sm ${labelClass}`}>
                        DOB: {p.dob || "-"}
                      </div>
                      <div className={`text-sm ${labelClass}`}>
                        Phone: {p.contact || "-"}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(p.tags || []).slice(0, 6).map((t, i) => (
                          <div
                            key={i}
                            style={{
                              backgroundColor: t.colour,
                              color: t.colour ? "#fff" : "#000",
                            }}
                            className="py-0.5 px-2 text-xs transition-colors duration-300 rounded-full"
                          >
                            {t?.tagName}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Hide same values toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="hide-same-values"
                    checked={hideSameValues}
                    onCheckedChange={setHideSameValues}
                  />
                  <Label
                    htmlFor="hide-same-values"
                    className={`text-sm ${labelClass} cursor-pointer`}
                  >
                    Hide fields with identical values
                  </Label>
                </div>

                {/* Field chooser */}
                <div className={`rounded-lg border ${borderClass}`}>
                  <div
                    className={`grid grid-cols-3 text-xs ${labelClass} border-b ${borderClass}`}
                  >
                    <div className="px-3 py-2">Field</div>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        onClick={() => selectAllFromPatient("A")}
                        className={`flex items-center gap-2 hover:opacity-70 transition-opacity`}
                        title="Select all from this patient"
                      >
                        <div
                          className={`w-4 h-4 rounded-full border-2 ${
                            isDarkMode ? "border-gray-400" : "border-gray-400"
                          } flex items-center justify-center`}
                        >
                          {selectedColumn === "A" && (
                            <div
                              className={`w-2 h-2 rounded-full ${
                                isDarkMode ? "bg-gray-400" : "bg-gray-600"
                              }`}
                            ></div>
                          )}
                        </div>
                        <span>{patientA.name || "Patient A"}</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        onClick={() => selectAllFromPatient("B")}
                        className={`flex items-center gap-2 hover:opacity-70 transition-opacity`}
                        title="Select all from this patient"
                      >
                        <div
                          className={`w-4 h-4 rounded-full border-2 ${
                            isDarkMode ? "border-gray-400" : "border-gray-400"
                          } flex items-center justify-center`}
                        >
                          {selectedColumn === "B" && (
                            <div
                              className={`w-2 h-2 rounded-full ${
                                isDarkMode ? "bg-gray-400" : "bg-gray-600"
                              }`}
                            ></div>
                          )}
                        </div>
                        <span>{patientB.name || "Patient B"}</span>
                      </button>
                    </div>
                  </div>

                  {baseFields.map(({ key, label }) => {
                    const valA = (patientA as any)[key];
                    const valB = (patientB as any)[key];
                    const isSame = areValuesEqual(valA, valB);

                    const isRequired = !isSame && !choices[key as string];

                    if (hideSameValues && isSame) return null;

                    return (
                      <div
                        key={key as string}
                        className={`grid grid-cols-3 items-center border-b ${borderClass} ${
                          isSame
                            ? isDarkMode
                              ? "bg-gray-800/50"
                              : "bg-gray-50"
                            : ""
                        }`}
                      >
                        <RadioGroup
                          value={choices[key as string] || ""}
                          onValueChange={(val) => {
                            setChoices((c) => ({
                              ...c,
                              [key as string]: val as "A" | "B",
                            }));
                            setSelectedColumn(null);
                          }}
                          className="contents"
                        >
                          <div
                            className={`px-3 py-2 text-sm ${labelClass} flex items-center gap-2`}
                          >
                            {label}
                            {isSame && (
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  isDarkMode
                                    ? "border-gray-600 text-gray-400"
                                    : "border-gray-300 text-gray-500"
                                }`}
                              >
                                Same
                              </Badge>
                            )}
                            {isRequired && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                          </div>
                          <label className="flex items-center gap-2 px-3 py-2">
                            <RadioGroupItem
                              value="A"
                              id={`rg-${key}-a`}
                              disabled={isSame}
                            />
                            <span
                              className={`text-sm ${
                                isSame ? labelClass : cellClass
                              }`}
                            >
                              {formatValue(valA)}
                            </span>
                          </label>
                          <label className="flex items-center gap-2 px-3 py-2">
                            <RadioGroupItem
                              value="B"
                              id={`rg-${key}-b`}
                              disabled={isSame}
                            />
                            <span
                              className={`text-sm ${
                                isSame ? labelClass : cellClass
                              }`}
                            >
                              {formatValue(valB)}
                            </span>
                          </label>
                        </RadioGroup>
                      </div>
                    );
                  })}

                  {/* Array fields with dual include checkboxes */}
                  {!(
                    hideSameValues &&
                    areValuesEqual(patientA.tags, patientB.tags, true)
                  ) && (
                    <div
                      className={`grid grid-cols-3 items-start border-b ${borderClass} ${
                        areValuesEqual(patientA.tags, patientB.tags, true)
                          ? isDarkMode
                            ? "bg-gray-800/50"
                            : "bg-gray-50"
                          : ""
                      }`}
                    >
                      <div
                        className={`px-3 py-2 text-sm ${labelClass} flex items-center gap-2`}
                      >
                        Tags
                        {areValuesEqual(patientA.tags, patientB.tags, true) && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              isDarkMode
                                ? "border-gray-600 text-gray-400"
                                : "border-gray-300 text-gray-500"
                            }`}
                          >
                            Same
                          </Badge>
                        )}
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={mergeTagsA}
                            onCheckedChange={(v) => {
                              setMergeTagsA(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.tags,
                              patientB.tags,
                              true,
                            )}
                          />
                          <span className="text-sm">
                            Keep {(patientA.tags || []).length} tag(s)
                          </span>
                        </label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(patientA.tags || []).map((t, i) => (
                            <div
                              key={i}
                              style={{
                                backgroundColor: t.colour,
                                color: t.colour ? "#fff" : "#000",
                              }}
                              className="py-0.5 px-2 text-xs transition-colors duration-300 rounded-full"
                            >
                              {t?.tagName}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={mergeTagsB}
                            onCheckedChange={(v) => {
                              setMergeTagsB(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.tags,
                              patientB.tags,
                              true,
                            )}
                          />
                          <span className="text-sm">
                            Keep {(patientB.tags || []).length} tag(s)
                          </span>
                        </label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(patientB.tags || []).map((t, i) => (
                            <div
                              key={i}
                              style={{
                                backgroundColor: t.colour,
                                color: t.colour ? "#fff" : "#000",
                              }}
                              className="py-0.5 px-2 text-xs transition-colors duration-300 rounded-full"
                            >
                              {t?.tagName}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {!(
                    hideSameValues &&
                    areValuesEqual(patientA.allergies, patientB.allergies, true)
                  ) && (
                    <div
                      className={`grid grid-cols-3 items-start ${
                        areValuesEqual(
                          patientA.allergies,
                          patientB.allergies,
                          true,
                        )
                          ? isDarkMode
                            ? "bg-gray-800/50"
                            : "bg-gray-50"
                          : ""
                      }`}
                    >
                      <div
                        className={`px-3 py-2 text-sm ${labelClass} flex items-center gap-2`}
                      >
                        Allergies
                        {areValuesEqual(
                          patientA.allergies,
                          patientB.allergies,
                          true,
                        ) && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              isDarkMode
                                ? "border-gray-600 text-gray-400"
                                : "border-gray-300 text-gray-500"
                            }`}
                          >
                            Same
                          </Badge>
                        )}
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={mergeAllergiesA}
                            onCheckedChange={(v) => {
                              setMergeAllergiesA(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.allergies,
                              patientB.allergies,
                              true,
                            )}
                          />
                          <span className="text-sm">
                            Keep {(patientA.allergies || []).length} allergie(s)
                          </span>
                        </label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(patientA.allergies || []).map((t, i) => (
                            <Badge
                              key={i}
                              variant="secondary"
                              className={
                                isDarkMode ? "bg-gray-700 text-gray-200" : ""
                              }
                            >
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={mergeAllergiesB}
                            onCheckedChange={(v) => {
                              setMergeAllergiesB(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.allergies,
                              patientB.allergies,
                              true,
                            )}
                          />
                          <span className="text-sm">
                            Keep {(patientB.allergies || []).length} allergie(s)
                          </span>
                        </label>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(patientB.allergies || []).map((t, i) => (
                            <Badge
                              key={i}
                              variant="secondary"
                              className={
                                isDarkMode ? "bg-gray-700 text-gray-200" : ""
                              }
                            >
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Consultations counts selection */}
                  {!(
                    hideSameValues &&
                    areValuesEqual(
                      patientA.consultationsCount,
                      patientB.consultationsCount,
                    )
                  ) && (
                    <div
                      className={`grid grid-cols-3 items-start border-b ${borderClass} ${
                        areValuesEqual(
                          patientA.consultationsCount,
                          patientB.consultationsCount,
                        )
                          ? isDarkMode
                            ? "bg-gray-800/50"
                            : "bg-gray-50"
                          : ""
                      }`}
                    >
                      <div
                        className={`px-3 py-2 text-sm ${labelClass} flex items-center gap-2`}
                      >
                        Consultations
                        {areValuesEqual(
                          patientA.consultationsCount,
                          patientB.consultationsCount,
                        ) && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              isDarkMode
                                ? "border-gray-600 text-gray-400"
                                : "border-gray-300 text-gray-500"
                            }`}
                          >
                            Same
                          </Badge>
                        )}
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={keepConsultationsA}
                            onCheckedChange={(v) => {
                              setKeepConsultationsA(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.consultationsCount,
                              patientB.consultationsCount,
                            )}
                          />
                          <span className="text-sm">
                            Keep {patientA.consultationsCount || 0}{" "}
                            consultation(s)
                          </span>
                        </label>
                      </div>
                      <div className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={keepConsultationsB}
                            onCheckedChange={(v) => {
                              setKeepConsultationsB(!!v);
                              setSelectedColumn(null);
                            }}
                            disabled={areValuesEqual(
                              patientA.consultationsCount,
                              patientB.consultationsCount,
                            )}
                          />
                          <span className="text-sm">
                            Keep {patientB.consultationsCount || 0}{" "}
                            consultation(s)
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Merged preview (always visible) */}
              <div
                className={`rounded-lg border ${borderClass} p-4 mt-3 min-h-[180px] ${
                  isDarkMode ? "bg-gray-900" : "bg-white"
                }`}
              >
                <div className={`text-sm ${labelClass} mb-3`}>
                  Merged preview
                </div>
                <div className={`text-sm ${cellClass} grid grid-cols-2 gap-4`}>
                  <div className="space-y-1">
                    <div>
                      <span className={labelClass}>Name:</span>{" "}
                      {mergedPreview.name || "-"}
                    </div>
                    <div>
                      <span className={labelClass}>NHS:</span>{" "}
                      {mergedPreview.nhs || "-"}
                    </div>
                    <div>
                      <span className={labelClass}>DOB:</span>{" "}
                      {mergedPreview.dob || "-"}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div>
                      <span className={labelClass}>Address:</span>{" "}
                      {mergedPreview.address || "-"}
                    </div>
                    <div>
                      <span className={labelClass}>Consultations:</span>{" "}
                      {mergedPreview.consultationsCount || 0}
                    </div>
                    <div>
                      <span className={labelClass}>Practitioner:</span>{" "}
                      {mergedPreview.practitioner || "-"}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className={`text-xs ${labelClass} mb-1`}>Tags:</div>
                  <div className="flex flex-wrap gap-1 overflow-y-auto max-h-16">
                    {(mergedPreview.tags || []).map((t, i) => (
                      <div
                        key={i}
                        style={{
                          backgroundColor: t.colour,
                          color: t.colour ? "#fff" : "#000",
                        }}
                        className="py-0.5 px-2 text-xs transition-colors duration-300 rounded-full"
                      >
                        {t?.tagName}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setShowWarningDialog(true)}
                  className="text-white bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!allRequiredFieldsSelected}
                >
                  Merge Records
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Warning Dialog - only render when main dialog is open */}
      {open && (
        <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
          <DialogContent
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            className={`!z-[2100] ${
              isDarkMode ? "bg-gray-900 text-white border-gray-700" : ""
            }`}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TriangleAlert className="w-5 h-5 text-amber-500" />
                Confirm Patient Merge
              </DialogTitle>
              <DialogDescription className={isDarkMode ? "text-gray-300" : ""}>
                <strong>Warning:</strong> This action cannot be undone. The
                selected patient records will be permanently merged, and one
                patient record will be archived. All data will be combined
                according to your selections.
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-2">
              <div className="p-3 border rounded-md bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-amber-200" : "text-amber-800"
                  }`}
                >
                  Are you sure you want to proceed with merging these patient
                  records?
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                className={
                  isDarkMode ? "border-gray-700 hover:bg-gray-800" : ""
                }
                variant="secondary"
                onClick={() => setShowWarningDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onConfirm(mergedPreview);
                }}
                className={`text-white bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed ${
                  mergePatientDetailsLoader && "opacity-2"
                }`}
                disabled={mergePatientDetailsLoader}
              >
                Yes, Merge Records
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
